import { useState } from 'react'
import { useQuery } from '@apollo/client'
import Image from 'next/legacy/image'
import { useTranslation } from 'next-i18next'

import { SEO, Section, Text, BoxButton, Carousel, Accordion } from '@components'
import CommonPageLayout from '@layouts/CommonPageLayout'

import styles from './style.module.css'

import { useIsWidthLarger } from '@utils/hooks'
import { getGiftCards } from '@gql/giftCardQuery'

function GiftCardHomeModule() {
  const isMedium = useIsWidthLarger(768)
  const { t } = useTranslation('giftCardLandingPage')
  const { data: { giftCards } = {} } = useQuery(getGiftCards)

  const [selectedIndex, setSelectedIndex] = useState(0)

  return (
    <>
      <SEO
        title="Gift Card"
        description="Feel More Special with Personalized Gift"
        keywords="Feel More Special with Personalized Gift"
      />

      <Section className={styles.heroContainer}>
        <Image
          alt="base gift card"
          src={
            isMedium
              ? 'https://static.base.co.id/gift-card-landing-page/header-gift-desktop-bg2.jpg'
              : 'https://static.base.co.id/gift-card-landing-page/header-gift-mobile-bg2.jpg'
          }
          layout="fill"
          objectFit="cover"
          priority
        />

        <div className={styles.contentContainer}>
          <div className={styles.heroTextContainer}>
            <Text
              tag="h1"
              variant="title"
              color="white"
              className={styles.heroTitle}
            >
              {t('giftCardLandingPage:section1.title')}
            </Text>

            <Text
              tag="h2"
              variant="heroDescription"
              color="white"
              className={styles.heroDescription}
            >
              {t('giftCardLandingPage:section1.description')}
            </Text>

            {isMedium && (
              <BoxButton
                color="whiteBlueShadow"
                link="/gift-card/buy"
                className={styles.button}
              >
                {t('giftCardLandingPage:button.send-gc')}
              </BoxButton>
            )}
          </div>

          <div className={styles.heroImage}>
            <Image
              alt="base gift card vouchers"
              src="https://static.base.co.id/header-giftcard.png"
              layout="responsive"
              height={712}
              width={691}
            />
          </div>

          {!isMedium && (
            <BoxButton
              color="whiteBlueShadow"
              link="/gift-card/buy"
              className={styles.button}
            >
              {t('giftCardLandingPage:button.send-gc')}
            </BoxButton>
          )}
        </div>
      </Section>

      <Section className={styles.giftCardContainer}>
        <Text
          tag="h3"
          variant="subtitle"
          className={styles.giftCardSectionTitle}
        >
          {t('giftCardLandingPage:section3.title')}
        </Text>

        {giftCards.length > 0 && (
          <div className={styles.carouselContainer}>
            <Carousel
              name="giftCardCarousel"
              arrowvariant="scroll"
              arrowPosition="top"
              color="black"
              onMoveChild={setSelectedIndex}
              childrenClassName={styles.carouselChild}
            >
              {giftCards.map(({ image }, index) => (
                <div
                  key={`base_news_${index}`}
                  className={`
                    ${styles.giftCardImage}
                    ${selectedIndex === index && styles.active}
                  `}
                >
                  <Image
                    alt={`gift-card-${index}`}
                    src={image}
                    height={233}
                    width={350}
                  />
                </div>
              ))}
            </Carousel>
          </div>
        )}

        <BoxButton
          color="whitePrimaryShadow"
          link="/gift-card/buy"
          className={`${styles.button} ${styles.sectionButton}`}
        >
          {t('giftCardLandingPage:button.send-gc')}
        </BoxButton>
      </Section>

      <Section className={styles.howToContainer}>
        <Text tag="h3" variant="subtitle">
          {t('giftCardLandingPage:section2.title')}
        </Text>

        <Text tag="h5" variant="heroDescription">
          {t('giftCardLandingPage:section2.description')}
        </Text>

        <div className={styles.stepContentContainer}>
          <div className={styles.step}>
            <Text
              variant="subtitle"
              color="deepBlue"
              className={styles.stepTitle}
            >
              {t('giftCardLandingPage:section2.step1.title')}
            </Text>

            <Text variant="heroDescription" className={styles.stepDescription}>
              {t('giftCardLandingPage:section2.step1.content')}
            </Text>
          </div>

          <div className={styles.step}>
            <Text
              variant="subtitle"
              color="deepBlue"
              className={styles.stepTitle}
            >
              {t('giftCardLandingPage:section2.step2.title')}
            </Text>

            <Text variant="heroDescription" className={styles.stepDescription}>
              {t('giftCardLandingPage:section2.step2.content')}
            </Text>
          </div>

          <div className={styles.step}>
            <Text
              variant="subtitle"
              color="deepBlue"
              className={styles.stepTitle}
            >
              {t('giftCardLandingPage:section2.step3.title')}
            </Text>

            <Text variant="heroDescription" className={styles.stepDescription}>
              {t('giftCardLandingPage:section2.step3.content')}
            </Text>
          </div>
        </div>

        <BoxButton
          color="whitePrimaryShadow"
          link="/gift-card/buy"
          className={`${styles.button} ${styles.sectionButton}`}
        >
          {t('giftCardLandingPage:button.send-gc')}
        </BoxButton>
      </Section>

      <Section className={styles.faqContainer}>
        <Text tag="h3" variant="subtitle">
          Frequently Asked Questions
        </Text>

        <Accordion multiple active={[0]}>
          {t('giftCardLandingPage:FAQ', { returnObjects: true }).map(
            ({ title, description }, index) => {
              return (
                <Accordion.Section key={`faq_gift_card_${index}`}>
                  <Accordion.Header>
                    {activeSection => {
                      const isActive = activeSection.includes(index)

                      return (
                        <div className={styles.faqHeader}>
                          <Text
                            variant="lightTitle"
                            className={styles.faqTitle}
                          >
                            {title}
                          </Text>

                          <span
                            className={`${styles.faqHeaderChevron} ${
                              isActive && styles.up
                            }`}
                          >
                            <Image
                              alt="close faq"
                              src="https://static.base.co.id/chevron-black.svg"
                              height={15}
                              width={28}
                            />
                          </span>
                        </div>
                      )
                    }}
                  </Accordion.Header>

                  <Accordion.Content>
                    <div>
                      <Text
                        variant="smallDescription"
                        className={styles.faqDescription}
                      >
                        {description}
                      </Text>
                    </div>
                  </Accordion.Content>
                </Accordion.Section>
              )
            }
          )}
        </Accordion>
      </Section>
    </>
  )
}

GiftCardHomeModule.getLayout = page => {
  return (
    <CommonPageLayout containerClassName={styles.container}>
      {page}
    </CommonPageLayout>
  )
}

export default GiftCardHomeModule
